import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, User, ChevronDown, Shield, Store, Bell, Users, Gavel, MessageSquare, Mail, Send, Globe, Calendar, CheckSquare, Wallet } from '../icons';
import { getNotifications, getMyInquiries, getMyUnreadCount, getVendorUnreadCount, getAppSettings, markNotificationRead, markAllNotificationsRead, getVendorPortal, getAdminInquiries, removeHelperRole, switchRole, markVendorInquiriesReadAll, markAdminInquiriesReadAll, getEmailAdminStats } from '../services/api';
import { onUnreadUpdate, UNREAD_CHANGED_EVENT } from '../services/socket';
import { useLanguage } from '../context/LanguageContext';
import LaganCalendar from './LaganCalendar';

/**
 * One nav item that opens a small menu of related links. Renders as a floating
 * dropdown on desktop; inside the mobile menu the CSS drops it inline so the
 * links simply appear in the stack rather than floating over it.
 */
function NavGroup({ label, items, onNavigate }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const active = items.some((i) => i.to === pathname);

  // Any navigation closes the menu, including a click on the link already active.
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <div className="nav-group">
      <button
        type="button"
        className={`nav-link nav-group-btn${active ? ' nav-group-active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {label} <ChevronDown size={14} className={open ? 'nav-group-chev open' : 'nav-group-chev'} />
      </button>
      {open && (
        <>
          <div className="nav-group-backdrop" onClick={() => setOpen(false)} />
          <div className="nav-group-menu">
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`nav-group-item${item.to === pathname ? ' active' : ''}`}
                onClick={() => { setOpen(false); onNavigate?.(); }}
              >
                <item.icon size={15} /> {item.label}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { lang, switchLang, t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [biddingEnabled, setBiddingEnabled] = useState(false);
  const [videosPageEnabled, setVideosPageEnabled] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [notifItems, setNotifItems] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [mailboxUnread, setMailboxUnread] = useState(0);
  const [laganOpen, setLaganOpen] = useState(false);
  const { pathname } = useLocation();

  // Close the mobile menu and user menu on any navigation. Only the links inside
  // the mobile panel closed it before, so tapping the logo, Login / Sign up or a
  // user-menu link left the panel open over the next page.
  useEffect(() => { setMenuOpen(false); setDropdownOpen(false); }, [pathname]);

  // Fetch bidding setting
  useEffect(() => {
    getAppSettings()
      .then(res => {
        setBiddingEnabled(res.data?.bidding_enabled === 'true');
        setVideosPageEnabled(res.data?.videos_page_enabled === 'true');
      })
      .catch(() => {});
  }, []);

  // Fetch mailbox unread count for admin
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    // Through the API client: a relative fetch('/api/...') resolves against
    // https://localhost inside the Android app and never reaches the server.
    const fetchMailbox = async () => {
      try {
        const res = await getEmailAdminStats();
        setMailboxUnread(parseInt(res.data?.unread || 0, 10));
      } catch { /* badge is best-effort */ }
    };
    fetchMailbox();
    const interval = setInterval(fetchMailbox, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Combined notification count (uses lightweight count endpoints, no side effects)
  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    let total = 0;
    try {
      if (user.role === 'couple' || user.role === 'helper') {
        const notifRes = await getNotifications({ unread: true, limit: 1 });
        total += notifRes.data.unreadCount || 0;
      }
      if (user.role === 'couple') {
        const inqRes = await getMyUnreadCount();
        total += inqRes.data.count || 0;
      }
      if (user.role === 'vendor') {
        const vendorRes = await getVendorUnreadCount();
        total += vendorRes.data.count || 0;
      }
      if (user.role === 'admin') {
        const adminRes = await getAdminInquiries();
        total += adminRes.data.filter(i => !i.admin_read).length;
      }
    } catch { /* ignore */ }
    setUnreadCount(total);
  }, [user]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Socket.IO: real-time unread push. The pushed number only covers inquiries
  // (and is computed differently for admins), so taking it as the badge value
  // dropped the other notifications and flip-flopped with the 15s poll. Treat
  // the push as a signal to refresh the badge right away instead.
  useEffect(() => {
    const unsub = onUnreadUpdate(() => { fetchUnreadCount(); });
    return () => unsub();
  }, [fetchUnreadCount]);

  // The floating chat marks a thread read when it opens it; refresh the badge
  // then rather than leaving it stale until the next poll.
  useEffect(() => {
    const onChanged = (e) => { if (e.detail?.from !== 'navbar') fetchUnreadCount(); };
    window.addEventListener(UNREAD_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(UNREAD_CHANGED_EVENT, onChanged);
  }, [fetchUnreadCount]);

  // Load notification items when bell opens
  const loadNotifications = async () => {
    if (notifLoading) return;
    setNotifLoading(true);
    try {
      const items = [];
      if (user.role === 'couple' || user.role === 'helper') {
        const res = await getNotifications({ limit: 20 });
        const notifs = res.data.notifications || res.data || [];
        notifs.forEach(n => items.push({
          id: `notif-${n.id}`,
          dbId: n.id,
          type: 'notification',
          title: n.title,
          message: n.message,
          time: n.created_at,
          isRead: n.is_read,
        }));
      }
      if (user.role === 'couple') {
        const res = await getMyInquiries();
        res.data.slice(0, 10).forEach(inq => items.push({
          id: `inq-${inq.id}`,
          type: 'inquiry',
          title: inq.business_name || 'Vendor Inquiry',
          message: inq.vendor_reply ? `Replied: "${inq.vendor_reply.substring(0, 60)}${inq.vendor_reply.length > 60 ? '...' : ''}"` : `Status: ${inq.status}`,
          time: inq.updated_at || inq.created_at,
          isRead: !inq.vendor_reply || inq.couple_read,
          link: '/my-inquiries',
        }));
      }
      if (user.role === 'vendor') {
        const res = await getVendorPortal();
        const inqs = res.data.inquiries || [];
        inqs.slice(0, 15).forEach(inq => items.push({
          id: `vinq-${inq.id}`,
          type: 'vendor-inquiry',
          title: `${inq.first_name} ${inq.last_name}`,
          message: inq.message ? inq.message.substring(0, 80) + (inq.message.length > 80 ? '...' : '') : 'New inquiry',
          time: inq.created_at,
          isRead: inq.vendor_read === true,
          link: `/vendor-portal?tab=inquiries&inq=${inq.id}`,
        }));
      }
      if (user.role === 'admin') {
        const res = await getAdminInquiries();
        res.data.slice(0, 20).forEach(inq => items.push({
          id: `adm-${inq.id}`,
          type: 'admin-inquiry',
          title: `${inq.first_name} ${inq.last_name} → ${inq.vendor_name}`,
          message: inq.message ? inq.message.substring(0, 80) + (inq.message.length > 80 ? '...' : '') : 'Inquiry',
          time: inq.created_at,
          isRead: !!inq.admin_read,
          link: `/admin?tab=inquiries&inq=${inq.id}`,
        }));
      }
      items.sort((a, b) => new Date(b.time) - new Date(a.time));
      setNotifItems(items);
    } catch { /* ignore */ }
    setNotifLoading(false);
  };

  const handleBellClick = () => {
    const next = !bellOpen;
    setBellOpen(next);
    if (next) loadNotifications();
  };

  const handleMarkAllRead = async () => {
    try {
      if (user.role === 'couple' || user.role === 'helper') {
        await markAllNotificationsRead();
      }
      // Vendor and admin badges count unread inquiries. Only clearing the badge
      // locally let the next 15s poll bring the same number straight back.
      if (user.role === 'vendor') await markVendorInquiriesReadAll();
      if (user.role === 'admin') await markAdminInquiriesReadAll();
      setNotifItems(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
      window.dispatchEvent(new CustomEvent(UNREAD_CHANGED_EVENT, { detail: { from: 'navbar' } }));
    } catch { /* ignore */ }
  };

  const goTo = (path) => {
    setBellOpen(false);
    // Force navigation even if already on the same page
    if (window.location.pathname === path) {
      window.location.reload();
    } else {
      navigate(path);
    }
  };

  const handleNotifClick = async (item) => {
    if (!item.isRead && item.type === 'notification') {
      try { await markNotificationRead(item.dbId); } catch {}
    }
    // Immediately decrement badge count
    if (!item.isRead) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    setNotifItems(prev => prev.map(n => n.id === item.id ? { ...n, isRead: true } : n));
    const dest = item.link || (item.type === 'notification' ? '/notifications' : null);
    setBellOpen(false);
    if (dest) {
      const currentFull = window.location.pathname + window.location.search;
      if (currentFull === dest) {
        window.location.reload();
      } else if (window.location.pathname === dest.split('?')[0] && dest.includes('?')) {
        // Same path but different query params — force full navigation
        window.location.href = dest;
      } else {
        navigate(dest);
      }
    }
  };

  const formatTime = (t) => {
    const d = new Date(t);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Dashboards this user can access beyond their current one (multi-role users)
  const ROLE_LABELS = { couple: t('nav.roleCouple'), helper: t('nav.roleHelper'), vendor_employee: t('nav.roleEmployee'), vendor: t('nav.roleVendor'), admin: t('nav.roleAdmin') };
  const ROLE_HOME = { couple: '/dashboard', helper: '/checklist', vendor_employee: '/my-projects', vendor: '/vendor-portal', admin: '/admin' };
  const otherRoles = (user?.availableRoles || []).filter(r => r !== user?.role);

  const handleSwitchRole = async (role) => {
    try {
      const res = await switchRole(role);
      localStorage.setItem('nepwed_token', res.data.token);
      // Full navigation so AuthContext re-fetches /me with the new active role
      window.location.href = ROLE_HOME[role] || '/dashboard';
    } catch {
      alert('Could not switch dashboard. Please try again.');
    }
  };

  const isAdmin = user?.role === 'admin';
  const isVendor = user?.role === 'vendor';
  const isCouple = user?.role === 'couple';
  const isHelper = user?.role === 'helper';
  const isVendorEmployee = user?.role === 'vendor_employee';
  const collabRole = user?.collaborator?.collaborator_role; // 'helper' or 'delegate'
  const isDelegate = isHelper && collabRole === 'delegate';

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <svg className="logo-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          <div className="logo-brand">
            <div className="logo-name"><span className="logo-text">NepWed</span><span className="logo-subtitle">.com</span></div>
            <div className="logo-tagline">Celebrate Love, The Nepali Way</div>
          </div>
        </Link>

        <div className={`navbar-links ${menuOpen ? 'active' : ''}`}>
          <Link to="/vendors" className="nav-link" onClick={() => setMenuOpen(false)}>
            {t('nav.vendors')}
          </Link>
          <Link to="/vendors/venues" className="nav-link" onClick={() => setMenuOpen(false)}>
            {t('nav.venues')}
          </Link>
          <div className="nav-lagan-wrapper">
            <button className="nav-link nav-lagan-btn" onClick={() => { setLaganOpen(o => !o); setMenuOpen(false); }}>
              <Calendar size={15} /> {lang === 'ne' ? 'लगन' : 'Lagan'}
            </button>
            {laganOpen && (
              <>
                <div className="nav-lagan-backdrop" onClick={() => setLaganOpen(false)} />
                <div className="nav-lagan-dropdown">
                  <LaganCalendar lang={lang} compact />
                </div>
              </>
            )}
          </div>
          {biddingEnabled && (
            <Link to="/bidding" className="nav-link" onClick={() => setMenuOpen(false)}>
              <Gavel size={16} /> {t('nav.bidding')}
            </Link>
          )}
          {videosPageEnabled && (
            <Link to="/wedding-videos" className="nav-link" onClick={() => setMenuOpen(false)}>
              {t('nav.videos')}
            </Link>
          )}
          {/* Couple-specific links. Eleven top-level items did not fit on one
              row, so everything past the dashboard lives in one grouped menu. */}
          {isCouple && (
            <>
              <Link to="/dashboard" className="nav-link" onClick={() => setMenuOpen(false)}>
                {t('nav.dashboard')}
              </Link>
              <NavGroup
                label={lang === 'ne' ? 'मेरो विवाह' : 'My Wedding'}
                items={[
                  { to: '/checklist',    icon: CheckSquare,    label: t('nav.checklist') },
                  { to: '/guests',       icon: Users,          label: t('nav.guests') },
                  { to: '/budget',       icon: Wallet,         label: t('nav.budget') },
                  { to: '/invitations',  icon: Send,           label: t('nav.invitations') },
                  { to: '/helpers',      icon: Users,          label: t('nav.team') },
                  { to: '/my-bookings',  icon: Store,          label: lang === 'ne' ? 'मेरो बुकिङ' : 'My Bookings' },
                  { to: '/my-inquiries', icon: MessageSquare,  label: t('nav.inquiries') },
                ]}
                onNavigate={() => setMenuOpen(false)}
              />
            </>
          )}
          {/* Delegate-specific links (full access except team management) */}
          {isDelegate && (
            <>
              <Link to="/dashboard" className="nav-link" onClick={() => setMenuOpen(false)}>
                {t('nav.dashboard')}
              </Link>
              <Link to="/checklist" className="nav-link" onClick={() => setMenuOpen(false)}>
                {t('nav.checklist')}
              </Link>
              <Link to="/guests" className="nav-link" onClick={() => setMenuOpen(false)}>
                {t('nav.guests')}
              </Link>
              <Link to="/budget" className="nav-link" onClick={() => setMenuOpen(false)}>
                {t('nav.budget')}
              </Link>
            </>
          )}
          {/* Helper-specific links (only checklist) */}
          {isHelper && !isDelegate && (
            <Link to="/checklist" className="nav-link" onClick={() => setMenuOpen(false)}>
              {t('nav.checklist')}
            </Link>
          )}
          {/* Vendor link */}
          {isVendor && (
            <Link to="/vendor-portal" className="nav-link vendor-nav" onClick={() => setMenuOpen(false)}>
              <Store size={16} /> {t('nav.myPortal')}
            </Link>
          )}
          {/* Vendor employee link */}
          {isVendorEmployee && (
            <Link to="/my-projects" className="nav-link vendor-nav" onClick={() => setMenuOpen(false)}>
              <Store size={16} /> {t('nav.myProjects')}
            </Link>
          )}
          {/* Admin link */}
          {isAdmin && (
            <Link to="/admin" className="nav-link admin-nav" onClick={() => setMenuOpen(false)}>
              <Shield size={16} /> {t('nav.adminPanel')}
            </Link>
          )}
        </div>

        <div className="navbar-right">
          {/* Language toggle */}
          <button
            className="lang-toggle"
            onClick={() => switchLang(lang === 'en' ? 'ne' : 'en')}
            title={lang === 'en' ? 'नेपालीमा हेर्नुहोस्' : 'Switch to English'}
          >
            <Globe size={16} />
            <span>{lang === 'en' ? 'ने' : 'EN'}</span>
          </button>
          {user ? (
            <>
              {isAdmin && (
                <div className="bell-wrapper" style={{ marginRight: 4 }}>
                  <button className="nav-notification-bell" title="Mailbox" onClick={() => navigate('/email-dashboard')}>
                    <Mail size={20} />
                    {mailboxUnread > 0 && <span className="notification-badge">{mailboxUnread > 99 ? '99+' : mailboxUnread}</span>}
                  </button>
                </div>
              )}
              {user && (
                <div className="bell-wrapper">
                  <button className="nav-notification-bell" title="Notifications" onClick={handleBellClick}>
                    <Bell size={20} />
                    {unreadCount > 0 && <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
                  </button>
                  {bellOpen && (
                    <>
                      <div className="notif-backdrop" onClick={() => setBellOpen(false)} />
                      <div className="notif-dropdown">
                        <div className="notif-dropdown-header">
                          <h4>{t('nav.notifications')}</h4>
                          {notifItems.some(n => !n.isRead) && (
                            <button className="notif-mark-all" onClick={handleMarkAllRead}>{t('nav.markAllRead')}</button>
                          )}
                        </div>
                        <div className="notif-dropdown-list">
                          {notifLoading ? (
                            <div className="notif-dropdown-empty"><div className="spinner-sm"></div></div>
                          ) : notifItems.length === 0 ? (
                            <div className="notif-dropdown-empty">
                              <Bell size={24} style={{ color: '#9ca3af' }} />
                              <p>{t('nav.noNotifications')}</p>
                            </div>
                          ) : (
                            notifItems.map(item => (
                              <div
                                key={item.id}
                                className={`notif-dropdown-item ${item.isRead ? 'read' : 'unread'}`}
                                onClick={() => handleNotifClick(item)}
                              >
                                <div className="notif-dropdown-icon">
                                  {item.type === 'inquiry' ? <MessageSquare size={14} /> :
                                   item.type === 'vendor-inquiry' ? <Mail size={14} /> :
                                   <Bell size={14} />}
                                </div>
                                <div className="notif-dropdown-content">
                                  <span className="notif-dropdown-title">{item.title}</span>
                                  <span className="notif-dropdown-msg">{item.message}</span>
                                  <span className="notif-dropdown-time">{formatTime(item.time)}</span>
                                </div>
                                {!item.isRead && <span className="notif-unread-dot"></span>}
                              </div>
                            ))
                          )}
                        </div>
                        <div className="notif-dropdown-footer">
                          <button onClick={() => goTo('/notifications')}>
                            {t('nav.viewAll')}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="user-menu" onClick={() => setDropdownOpen(!dropdownOpen)}>
              {isAdmin ? <Shield size={18} /> : isVendor ? <Store size={18} /> : <User size={18} />}
              <span>{user.first_name}</span>
              <span className={`role-indicator ${user.role}`}>
                {({ couple: t('nav.roleCouple'), vendor: t('nav.roleVendor'), helper: t('nav.roleHelper'), admin: t('nav.roleAdmin'), vendor_employee: t('nav.roleEmployee') })[user.role] || user.role}
              </span>
              <ChevronDown size={14} />
              {dropdownOpen && (
                <div className="dropdown-menu">
                  {isCouple && (
                    <>
                      <Link to="/dashboard" className="dropdown-item">{t('nav.myWedding')}</Link>
                      <Link to="/my-bookings" className="dropdown-item">{lang === 'ne' ? 'मेरो बुकिङ' : 'My Bookings'}</Link>
                      <Link to="/my-inquiries" className="dropdown-item">{t('nav.myInquiries')}</Link>
                      <Link to="/media" className="dropdown-item">{t('nav.photoVideo')}</Link>
                      <Link to="/wedding-website" className="dropdown-item">{t('nav.weddingWebsite')}</Link>
                    </>
                  )}
                  {isDelegate && (
                    <>
                      <Link to="/dashboard" className="dropdown-item">{t('nav.weddingDashboard')}</Link>
                      <Link to="/media" className="dropdown-item">{t('nav.photoVideo')}</Link>
                    </>
                  )}
                  {isHelper && !isDelegate && (
                    <>
                      <Link to="/checklist" className="dropdown-item">{t('nav.checklist')}</Link>
                      <button onClick={async () => {
                        if (!window.confirm(t('nav.removeHelperRole') + '?')) return;
                        try { await removeHelperRole(); logout(); navigate('/login'); } catch { alert('Failed to remove helper role'); }
                      }} className="dropdown-item" style={{ color: '#dc2626', border: 'none', background: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>{t('nav.removeHelperRole')}</button>
                    </>
                  )}
                  {isVendor && (
                    <Link to="/vendor-portal" className="dropdown-item">{t('nav.vendorPortal')}</Link>
                  )}
                  {isVendorEmployee && (
                    <Link to="/my-projects" className="dropdown-item">{t('nav.myProjects')}</Link>
                  )}
                  {isAdmin && (
                    <Link to="/admin" className="dropdown-item">{t('nav.adminDashboard')}</Link>
                  )}
                  {otherRoles.length > 0 && (
                    <>
                      <div className="dropdown-section-label">{t('nav.switchDashboard')}</div>
                      {otherRoles.map(r => (
                        <button
                          key={r}
                          onClick={() => handleSwitchRole(r)}
                          className="dropdown-item"
                          style={{ border: 'none', background: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}
                        >
                          {ROLE_LABELS[r] || r}
                        </button>
                      ))}
                    </>
                  )}
                  <button onClick={handleLogout} className="dropdown-item logout-btn">{t('nav.logout')}</button>
                </div>
              )}
            </div>
            </>
          ) : (
            <div className="auth-buttons">
              <Link to="/login" className="btn btn-outline">{t('nav.login')}</Link>
              <Link to="/register" className="btn btn-primary">{t('nav.signup')}</Link>
            </div>
          )}
          <button className="mobile-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
